import React from "react"
import { useQuery } from "react-query"
import Axios from "axios"
import { useTranslation } from "react-i18next"
import {
  MdDownload,
  MdRefresh,
  MdPeople,
  MdHome,
  MdAssignment,
  MdFolder,
  MdCalculate,
  MdReceipt,
  MdEvent,
  MdSecurity,
  MdNotes,
  MdPayment,
  MdSchedule,
  MdInventory,
  MdBuild,
  MdShoppingCart,
  MdBarChart,
  MdClose
} from "react-icons/md"
import { Button } from "components/button"
import { Status } from "components/status"
import { useAuthContext } from "contexts/auth"
import { useModalContext } from "components/modal"
import AreYouSureModal from "components/areYouSureModal"
import convertCVD from "utils/convertCVD"

interface ExportJob {
  job_id: number
  export_type: string
  status: string
  status_color: string | null
  progress: number
  total_records: number | null
  file_url: string | null
  file_name: string | null
  file_size_bytes: number | null
  created_at: string
  error_message: string | null
}

const RecentExports = () => {
  const { t } = useTranslation()
  const tBase = "views.admin.export.components.RecentExports"
  const tr = (key: string) => t(`${tBase}.${key}`)
  const { cvd_type } = useAuthContext()
  const { setModal } = useModalContext()

  const { data, isLoading, refetch } = useQuery<{ jobs: ExportJob[] }>(
    "export-jobs",
    async () => {
      const response = await Axios.get(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/export-tool/jobs?limit=20`
      )
      return response.data
    },
    {
      // Dynamic polling: faster when there are active jobs, slower otherwise
      refetchInterval: (data) => {
        const jobs = data?.jobs || []
        const hasActiveJobs = jobs.some((job) => 
          job.status === "processing" || job.status === "pending"
        )
        // Only poll frequently if there are truly active jobs (pending or processing)
        // Cancelled jobs are marked as "failed" so they won't trigger frequent polling
        return hasActiveJobs ? 2000 : 30000 // Poll every 2 seconds if active, every 30 seconds otherwise
      }
    }
  )

  // Icon mapping for export types (matches index.tsx)
  const getExportTypeIcon = (type: string) => {
    const iconMap: Record<string, React.ComponentType> = {
      customers: MdPeople,
      addresses: MdHome,
      requests: MdAssignment,
      projects: MdFolder,
      estimates: MdCalculate,
      invoices: MdReceipt,
      appointments: MdEvent,
      warranties: MdSecurity,
      notes: MdNotes,
      payments: MdPayment,
      service_plans: MdSchedule,
      inventory: MdInventory,
      services: MdBuild,
      groups: MdShoppingCart,
      customer_summary: MdBarChart,
      files: MdFolder // Default icon for files
    }
    return iconMap[type] || MdFolder
  }

  const formatExportType = (type: string) => {
    if (type === "addresses") return tr("Properties")
    return type
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ")
  }

  const formatFileSize = (bytes: number | null) => {
    if (!bytes) return "-"
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleString()
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "completed":
        return tr("Completed")
      case "failed":
        return tr("Failed")
      case "processing":
        return tr("Processing")
      case "pending":
        return tr("Pending")
      case "expired":
        return tr("Expired")
      default:
        return status
    }
  }

  const handleDownload = async (jobId: number) => {
    try {
      const response = await Axios.get(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/export-tool/download/${jobId}`
      )
      if (response.data.file_url) {
        window.open(response.data.file_url, "_blank")
      }
    } catch (error) {
      console.error("Download failed:", error)
    }
  }

  const handleCancel = (jobId: number) => {
    setModal({
      label: tr("Cancel Export"),
      component: (
        <AreYouSureModal
          confirmationMessage={tr("Are you sure you want to cancel this export?")}
          onConfirm={async () => {
            try {
              await Axios.delete(
                `${process.env.REACT_APP_SERVER_URL}/api/v1/export-tool/jobs/${jobId}`
              )
              // Refetch to update the UI
              refetch()
            } catch (error: any) {
              console.error("Cancel failed:", error)
              alert(error.response?.data?.message || tr("Failed to cancel export"))
            }
          }}
        />
      ),
      props: {
        modalWidth: "500px"
      }
    })
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-800">{tr("Recent Exports")}</h2>
        <Button
          size="small"
          onClick={() => refetch()}
          className="text-gray-500 hover:text-gray-700"
        >
          <MdRefresh className="text-lg" />
        </Button>
      </div>

      {isLoading ? (
        <div className="text-center py-8 text-gray-500">{tr("Loading...")}</div>
      ) : !data?.jobs?.length ? (
        <div className="text-center py-8 text-gray-500">{tr("No recent exports")}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-2 text-sm font-medium text-gray-500">
                  {tr("Type")}
                </th>
                <th className="text-left py-3 px-2 text-sm font-medium text-gray-500">
                  {tr("Status")}
                </th>
                <th className="text-left py-3 px-2 text-sm font-medium text-gray-500">
                  {tr("Progress")}
                </th>
                <th className="text-left py-3 px-2 text-sm font-medium text-gray-500">
                  {tr("Records")}
                </th>
                <th className="text-left py-3 px-2 text-sm font-medium text-gray-500">
                  {tr("Size")}
                </th>
                <th className="text-left py-3 px-2 text-sm font-medium text-gray-500">
                  {tr("Created")}
                </th>
                <th className="text-right py-3 px-2 text-sm font-medium text-gray-500">
                  {tr("Action")}
                </th>
              </tr>
            </thead>
            <tbody>
              {data.jobs.map((job) => (
                <tr key={job.job_id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-2">
                    <div className="flex items-center gap-2">
                      {(() => {
                        const IconComponent = getExportTypeIcon(job.export_type)
                        return <IconComponent className="text-gray-500 text-lg" />
                      })()}
                      <span className="font-medium text-gray-700">
                        {formatExportType(job.export_type)}
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-2">
                    {job.status_color ? (
                      <Status color={convertCVD(job.status_color, cvd_type)}>
                        {convertCVD(job.status_color, cvd_type) === "255,255,255" ? (
                          <span className="text-black">{getStatusLabel(job.status)}</span>
                        ) : (
                          getStatusLabel(job.status)
                        )}
                      </Status>
                    ) : (
                      <span className="text-sm text-gray-500">{getStatusLabel(job.status)}</span>
                    )}
                  </td>
                  <td className="py-3 px-2">
                    {job.status === "processing" || job.status === "pending" ? (
                      <div className="w-24">
                        <div className="bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-500 rounded-full h-2 transition-all duration-300"
                            style={{ width: `${Math.max(0, Math.min(100, job.progress || 0))}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-500">
                          {job.progress !== undefined && job.progress !== null ? `${job.progress}%` : "0%"}
                        </span>
                      </div>
                    ) : job.status === "completed" ? (
                      <span className="text-sm text-green-600">100%</span>
                    ) : (
                      <span className="text-sm text-gray-400">-</span>
                    )}
                  </td>
                  <td className="py-3 px-2 text-sm text-gray-600">
                    {job.total_records?.toLocaleString() || "-"}
                  </td>
                  <td className="py-3 px-2 text-sm text-gray-600">
                    {formatFileSize(job.file_size_bytes)}
                  </td>
                  <td className="py-3 px-2 text-sm text-gray-500">
                    {formatDate(job.created_at)}
                  </td>
                  <td className="py-3 px-2 text-right">
                    {job.status === "completed" ? (
                      job.file_url ? (
                        <Button
                          size="small"
                          primary
                          onClick={() => handleDownload(job.job_id)}
                        >
                          <MdDownload className="mr-1" />
                          {tr("Download")}
                        </Button>
                      ) : (
                        <span className="text-sm text-gray-400">{tr("No file available")}</span>
                      )
                    ) : job.status === "failed" ? (
                      <span className="text-sm text-red-500" title={job.error_message || ""}>
                        {tr("Error")}
                      </span>
                    ) : job.status === "pending" || job.status === "processing" ? (
                      <Button
                        size="small"
                        onClick={() => handleCancel(job.job_id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <MdClose className="mr-1" />
                        {tr("Cancel")}
                      </Button>
                    ) : (
                      <span className="text-sm text-gray-400">{tr("Processing...")}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default RecentExports
